import {
  KuxFilteredCategoriesDTO,
  PresentationAdditionalTypeEnum,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '@kaltura-ott/tvpil';

import type { Category, CategoryChildren } from './category-children';
import type { PageStore } from './page';
import type { WidgetStore } from './widgets';

/**
 * @category Store
 */
export abstract class CategoryStore<T extends Category = CategoryChildren> {
  /**
   * type of the store from [[UIDAStructureTypeEnum]]. Overridden in child stores.
   */
  type: UIDAStructureTypeEnum = UIDAStructureTypeEnum.root;
  /**
   * subetype of the store from [[UIDAPresentationTypeEnum]]. Overridden in child stores.
   */
  subtype: UIDAPresentationTypeEnum | PresentationAdditionalTypeEnum = UIDAPresentationTypeEnum.container;
  id = 0;
  referenceId = '';
  name = '';
  channels: number[] = [];
  aggregatedKSQL = '';
  uiDesignElements: KuxFilteredCategoriesDTO['uiDesignElements'];
  children: T[] = [];
  parentalPinProtected = false;
  hideLogoutButton = false;
  displayName = '';

  public categoryTree: KuxFilteredCategoriesDTO;
  public parentCategoryTree?: KuxFilteredCategoriesDTO;

  constructor(categoryTree: KuxFilteredCategoriesDTO, parentCategoryTree?: KuxFilteredCategoriesDTO) {
    this.id = categoryTree.id;
    this.displayName = categoryTree.displayName;
    this.referenceId = categoryTree.referenceId;
    this.categoryTree = categoryTree;
    this.parentCategoryTree = parentCategoryTree;
    this.subtype = categoryTree.subtype;
    this.name = categoryTree.name;
    this.channels = categoryTree.channels;
    this.aggregatedKSQL = categoryTree.aggregatedKSQL;
    this.uiDesignElements = categoryTree.uiDesignElements;
    this.hideLogoutButton = categoryTree.hideLogoutButton;
    this.parentalPinProtected = categoryTree.parentalPinProtected;
  }

  protected filterChildrenByType(filterType: UIDAStructureTypeEnum) {
    if (!this.children) {
      return;
    }
    return this.children.filter(({ type }) => type === filterType);
  }

  public get internalWidgets(): WidgetStore[] {
    return this.filterChildrenByType(UIDAStructureTypeEnum.widget) as unknown as WidgetStore[];
  }

  public get internalPages(): PageStore[] {
    return this.filterChildrenByType(UIDAStructureTypeEnum.page) as unknown as PageStore[];
  }

  /**
   * if id is undefined returned first page
   */
  public getInternalPageById(searchedId?: string): PageStore {
    const page = this.internalPages.find(({ referenceId }) => !searchedId || referenceId === searchedId);
    if (page === undefined) {
      throw new Error(`InternalPage ${searchedId} not found`);
    }
    return page;
  }
}
